/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Box } from '@mui/material';
import React from 'react';
import type { SortableEvent } from 'react-sortablejs';
import { ReactSortable } from 'react-sortablejs';
import { breakpoints } from '@opengeoweb/theme';
import type {
  DistrictsType,
  MaritimeWarningsDomain,
  MaritimeWarningItem,
  MaritimeWarningItemAttributes,
  MaritimeWarningsConfig,
} from '../types';
import { WarningListItem } from './WarningListItem';
import { useWarningsTranslation } from '../../../utils/i18n';
import WarningListHeaderContent from '../../PublicWarningList/WarningListHeaderContent';

export interface WarningListProps {
  warnings: MaritimeWarningItem[];
  onAddSelectedAreas: (id: string) => void;
  onRemoveArea: (areaId: string) => void;
  onRemoveWarning: (id: string) => void;
  onChangeWarning: (
    id: string,
    newValues: Partial<MaritimeWarningItemAttributes>,
  ) => void;
  onToggleSelectArea: (areaId: string) => void;
  onSelectMultipleAreas: (areaIds: string[]) => void;
  onMoveWarning: (oldIndex: number, newIndex: number) => void;
  selectedAreaIds: string[];
  areas: DistrictsType[];
  isReadOnly: boolean;
  shouldShowDistricts?: boolean;
  maritimeConfig: MaritimeWarningsConfig;
  domain: MaritimeWarningsDomain;
}

export const WarningList: React.FC<WarningListProps> = ({
  warnings,
  areas,
  selectedAreaIds,
  onAddSelectedAreas,
  onRemoveWarning,
  onRemoveArea,
  onChangeWarning,
  onToggleSelectArea,
  onSelectMultipleAreas,
  onMoveWarning,
  isReadOnly,
  shouldShowDistricts = false,
  maritimeConfig,
  domain,
}: WarningListProps) => {
  const { t } = useWarningsTranslation();

  const onSort = React.useCallback(
    (event: SortableEvent) => {
      onMoveWarning(event.oldIndex!, event.newIndex!);
    },
    [onMoveWarning],
  );

  return (
    <Box
      data-testid="warninglist"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        marginBottom: 1,
        '--grid-columns': '16px 150px 60px 2fr 150px 150px 0.5fr 0.5fr',
      }}
    >
      <Box
        sx={{
          display: 'none',
          [`@container maritime (min-width: ${breakpoints.tablet}px)`]: {
            display: 'grid',
          },

          gridTemplateColumns: 'var(--grid-columns)',
          gap: 0.5,
          paddingBlockEnd: 1,
          justifyItems: 'center',
          '& > *': {
            width: 'auto !important',
          },
        }}
      >
        <WarningListHeaderContent
          sx={{
            gridColumnStart: 2,
          }}
          title={t('maritime-header-direction')}
        />
        <WarningListHeaderContent title={t('maritime-header-windforce')} />
        <WarningListHeaderContent
          title={
            shouldShowDistricts
              ? t('maritime-header-districts')
              : t('maritime-header-areas')
          }
        />
        <WarningListHeaderContent title={t('maritime-header-change')} />
        <WarningListHeaderContent title={t('maritime-header-direction')} />
      </Box>

      <ReactSortable
        animation={200}
        direction="vertical"
        handle=".handle"
        list={warnings}
        onSort={onSort}
        setList={() => {}}
        style={{
          minHeight: 0,
          overflow: 'auto',
        }}
        tag="div"
      >
        {warnings.map((warning) => {
          return (
            <WarningListItem
              areas={areas}
              domain={domain}
              isReadOnly={isReadOnly}
              key={warning.id}
              maritimeConfig={maritimeConfig}
              onAddClick={onAddSelectedAreas}
              onChange={onChangeWarning}
              onRemove={onRemoveWarning}
              onRemoveArea={onRemoveArea}
              onSelectMultipleAreas={onSelectMultipleAreas}
              onToggleSelectArea={onToggleSelectArea}
              selectedAreaIds={selectedAreaIds}
              shouldShowDistricts={shouldShowDistricts}
              warning={warning}
            />
          );
        })}
      </ReactSortable>
    </Box>
  );
};
